import { Fragment, useState } from 'react';
import type { PreisToolLegendThresholds } from '@/api/preis-tool/preis-tool.types';
import { DsButton } from '@/modules/internal-v2/ds';
import {
  LEGEND_ORDER,
  ROW_STATE_CLASS,
  ROW_STATE_LABEL,
  rowStateDescription,
  type LegendLimits,
  type RowState,
} from '../row-states';
import { LegendThresholdsDialog } from './legend-thresholds-dialog.component';

interface LegendFilterProps {
  /** Active states; several are combined as a union. */
  selected: readonly RowState[];
  onToggle: (state: RowState) => void;
  /** Rows per state among the rows of every other filter. */
  counts: Record<RowState, number>;
  limits: LegendLimits;
  thresholds: PreisToolLegendThresholds | undefined;
  /** `PREIS_TOOL_MANAGE`: may change the limits. */
  canManage: boolean;
}

/**
 * The colour legend above the article list (EOS-61): every entry is a toggle
 * that narrows the list to its state and shows how many rows have it. The
 * swatch carries the same class as the row, so legend and row share a colour.
 */
export function LegendFilter({ selected, onToggle, counts, limits, thresholds, canManage }: LegendFilterProps) {
  const [limitsOpen, setLimitsOpen] = useState(false);

  return (
    <div className="pt-legend" role="group" aria-label="Farblegende – Liste nach Zustand eingrenzen">
      {LEGEND_ORDER.map((state) => {
        const on = selected.includes(state);
        return (
          <Fragment key={state}>
            {state === 'deal' && <span className="pt-filter-sep" aria-hidden />}
            <button
              type="button"
              className="pt-legend-btn"
              aria-pressed={on}
              onClick={() => onToggle(state)}
              title={`${rowStateDescription(state, limits)} ${on ? 'Klick hebt die Eingrenzung auf.' : 'Klick zeigt nur diese Artikel.'}`}
            >
              <span className={`pt-legend-sw ${ROW_STATE_CLASS[state]}`} aria-hidden />
              {ROW_STATE_LABEL[state]}
              <span className="count">{counts[state].toLocaleString('de-DE')}</span>
            </button>
          </Fragment>
        );
      })}
      <DsButton
        size="small"
        variant="ghost"
        className="pt-legend-limits"
        onClick={() => setLimitsOpen(true)}
        title="Grenzen für „Deal“ und „Niedrige Marge“ – gelten für alle Nutzer"
      >
        Grenzen…
      </DsButton>
      {limitsOpen && (
        <LegendThresholdsDialog
          thresholds={thresholds}
          canEdit={canManage}
          onClose={() => setLimitsOpen(false)}
        />
      )}
    </div>
  );
}
